import { getPricingPlansPublicAPI } from '@wix/pricing-plans-tpa-api';
import type { IWixAPI } from '@wix/yoshi-flow-editor';
import { createAction } from '@wix/communities-blog-client-common';
import { OOI_EXPERIMENTS } from '@app/experiments';
import { blogAppDefId } from '@app/external/common/constants/apps';
import { REFERRAL_BLOG_POST_PAYWALL } from '../../common/constants/paid-plans';
import { fetchPlanIdsForPost } from '../../common/services/fetch-plan-ids-for-post';
import type { NormalizedPost } from '../../common/types';
import { type PostPageThunkAction } from '../types';

const fetchPricingPlansRequest = createAction('pricingPlans/FETCH_REQUEST');

export const fetchPricingPlansUrlSuccess = createAction(
  'pricingPlans/FETCH_SUCCESS',
  (payload: string | undefined) => payload,
);

const fetchPricingPlansUrlFailure = createAction('pricingPlans/FETCH_FAILURE');

const getOptions = ({
  translations,
  post,
  planIds,
}: {
  translations: Record<string, string>;
  post: NormalizedPost;
  planIds: string[];
}) => {
  const content = {
    title: translations['subscription.thanks-page.title'],
    message: translations['subscription.thanks-page.text'],
    cta: translations['subscription.thanks-page.continue-button'],
  };

  return {
    planIds,
    checkout: {
      thankYouPage: {
        content,
        navigation: {
          type: 'section' as const,
          options: {
            sectionId: 'post',
            appDefinitionId: blogAppDefId,
            state: post.slug,
          },
        },
      },
    },
    biOptions: {
      referralInfo: REFERRAL_BLOG_POST_PAYWALL,
      referralId: post.id,
    },
  };
};

export default function fetchPricingPlansUrl(post: NormalizedPost): PostPageThunkAction {
  return async (dispatch, _, { platformApi, flowAPI }) => {
    dispatch(fetchPricingPlansRequest());
    try {
      const isNileEnabled = flowAPI.experiments.enabled(
        OOI_EXPERIMENTS.PROVISION_NILE_DRAFT_POSTS,
      );
      const planIds = isNileEnabled
        ? await fetchPlanIdsForPost(flowAPI.httpClient, post.id)
        : post.paidPlansGroupIds ?? [];

      const options = getOptions({
        translations: flowAPI.translations.all,
        post,
        planIds,
      });

      const pricingPlansApi = await getPricingPlansPublicAPI(platformApi as unknown as IWixAPI);
      const url = await pricingPlansApi.getPricingPageUrl(options);
      dispatch(fetchPricingPlansUrlSuccess(url));
    } catch (error) {
      dispatch(fetchPricingPlansUrlFailure(error));
    }
  };
}
